import { MeasurementType, UnitOfMeasurement } from '@/types/helpers';
import { airchefApi } from '../../api';
import { ConversiontableListData, SymbolsByUnitListData } from '../../api/airchef/Api';
import { createApiCache } from '../../api/ApiCache';
import { fallback, fallbackByType } from './fallbacktable';

type UnitRequestType = 'conversion' | 'validSystems';
type UnitResponseType = ConversiontableListData | SymbolsByUnitListData;

type UnitKeyMap = {
  conversion: ConversiontableListData;
  validSystems: SymbolsByUnitListData;
};

type UnitOptionsConversion = {
  requestType: 'conversion';
};

type UnitOptionsSystems = {
  requestType: 'validSystems';
};

type UnitOptions = UnitOptionsSystems | UnitOptionsConversion;

const typesCache = {
  extras: ['other', 'temperature'],
  knownTypes: new Set([
    'length',
    'mass',
    'power',
    'energy',
    'temperature',
    'volume',
    'other',
    'area',
    'volumetricflowrate',
    'pressure',
  ]),
};

async function fetchUnitSystem(opts: UnitOptions): Promise<UnitResponseType> {
  if (opts.requestType === 'conversion') {
    const response = await airchefApi.conversionTable.conversiontableList();
    return response.data;
  }
  if (opts.requestType === 'validSystems') {
    const response = await airchefApi.conversionTable.symbolsByUnitList();

    const data = response.data;

    typesCache.knownTypes = new Set([...typesCache.extras, ...Object.keys(data)].map((k) => k.toLocaleLowerCase()));

    return data;
  }
  throw new Error('Unknown requestType');
}

export const unitSystemCache = createApiCache<
  UnitOptions,
  UnitResponseType,
  UnitResponseType,
  UnitRequestType,
  UnitKeyMap
>(
  'units',
  fetchUnitSystem,
  (opts) => opts.requestType,
  (data) => data,
  (set) => {
    // Populate cache with hardcoded defaults.
    // These will get overwritten if/when the api responds with new data.
    // The fallbacks are here to make sure basics still work even if the api does not.
    set('conversion', fallback);
    set('validSystems', fallbackByType);
  },
);

export function getValidUnits() {
  const vs = unitSystemCache.get('validSystems');
  if (!vs) {
    return {};
  }

  return vs;
}

export function getValidTypes() {
  return typesCache.knownTypes;
}

export function getConversionTable() {
  const ct = unitSystemCache.get('conversion');
  if (!ct) {
    return {};
  }

  return ct;
}

export const conversionCache = new Map<string, Record<string, string>>();

function splitConversionmap(data: ConversiontableListData) {
  conversionCache.clear();
  for (const unit of Object.keys(data)) {
    conversionCache.set(unit, data[unit]);
  }
}

function isConversiontableListData(
  data: ConversiontableListData | SymbolsByUnitListData,
): data is ConversiontableListData {
  const sample = Object.values(data)[0];
  return !!sample && !Array.isArray(sample);
}

function splitConversionmapListener(data: ConversiontableListData | SymbolsByUnitListData) {
  if (!isConversiontableListData(data)) {
    return;
  }

  splitConversionmap(data);
}

unitSystemCache.addListener('conversion', splitConversionmapListener);

export function getConversionsForUnit(unit: UnitOfMeasurement<MeasurementType>) {
  return conversionCache.get(unit);
}
